import { Alert, Modal, Stack } from '@mantine/core'
import { IconExclamationMark } from '@tabler/icons-react'
import { useEffect } from 'react'

import { useAutoRefillConfig } from '@/hooks/useAutoRefillConfig'
import type { PaymentMethod, Plan } from '@/services/api/api.types'

import { BillingPlanSelection } from './BillingPlanSelection'
import { PaymentFlowContent } from './PaymentFlowContent'

interface AutoRefillConfigModalProps {
  opened: boolean
  onClose: () => void
  plans: Plan[]
  currentPlan?: Plan
  onPlanUpdate: (planId: string, autoRefill: boolean) => Promise<void>
  paymentMethod?: PaymentMethod | null
  currentAutoRefillEnabled?: boolean
  currentThresholdCredits?: number | null
}

export function AutoRefillConfigModal({
  opened,
  onClose,
  plans,
  currentPlan,
  onPlanUpdate,
  paymentMethod = null,
  currentAutoRefillEnabled = false,
  currentThresholdCredits = null,
}: AutoRefillConfigModalProps) {
  const {
    selectedPlan,
    selectedPlanData,
    autoRefillEnabled,
    thresholdCredits,
    showPaymentForm,
    clientSecret,
    isLoading,
    error,
    setupPaymentMethodMutation,
    handlePlanSelect,
    handleAutoRefillChange,
    handleThresholdChange,
    handleSave,
    handlePaymentSuccess,
    handleBackFromPayment,
    resetModalState,
  } = useAutoRefillConfig({
    plans,
    currentPlan,
    paymentMethod,
    currentAutoRefillEnabled,
    currentThresholdCredits,
    onPlanUpdate,
    onClose,
  })

  // Reset modal state when opened
  useEffect(() => {
    if (opened) {
      resetModalState()
    }
  }, [opened, resetModalState])

  const handleCancel = () => {
    onClose()
  }

  // Determine modal configuration based on current state
  const getModalConfig = () => {
    if (showPaymentForm && selectedPlanData) {
      return {
        title: 'Add Payment Method',
        size: 'xl' as const,
        maxWidth: '700px',
        styles: {
          body: { padding: '1.5rem' },
          header: { padding: '1rem 1.5rem 0 1.5rem' },
        },
      }
    }
    return {
      title: 'Update Auto-Refill Plan',
      size: 'xxl' as const,
      maxWidth: undefined,
      styles: undefined,
    }
  }

  const modalConfig = getModalConfig()

  // Render modal content based on current state
  const renderModalContent = () => {
    // Use shared payment flow component
    if (showPaymentForm && selectedPlanData) {
      return (
        <PaymentFlowContent
          showPaymentForm={showPaymentForm}
          clientSecret={clientSecret}
          selectedPlanData={selectedPlanData}
          plans={plans}
          autoRefill={true}
          isLoading={isLoading}
          hasError={!!setupPaymentMethodMutation.error}
          onPaymentSuccess={handlePaymentSuccess}
          onBackFromPayment={handleBackFromPayment}
          onRetry={() => setupPaymentMethodMutation.mutate()}
          loadingText="Setting up auto-refill payment..."
          mainText="Enter your payment details to finalize your auto-refill setup"
        />
      )
    }

    // Default state - plan selection
    return (
      <Stack gap="md">
        {error && (
          <Alert variant="light" color="red" title="Error" icon={<IconExclamationMark />}>
            {error}
          </Alert>
        )}
        <BillingPlanSelection
          plans={plans}
          selectedPlan={selectedPlan}
          autoRefillEnabled={autoRefillEnabled}
          thresholdCredits={thresholdCredits}
          onPlanSelect={handlePlanSelect}
          onAutoRefillChange={handleAutoRefillChange}
          onThresholdChange={handleThresholdChange}
          onSave={handleSave}
          onCancel={handleCancel}
          isUpdating={isLoading}
          paymentMethod={paymentMethod}
        />
      </Stack>
    )
  }

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title={modalConfig.title}
      size={modalConfig.size}
      maw={modalConfig.maxWidth}
      centered
      closeOnClickOutside={!isLoading}
      closeOnEscape={!isLoading}
      styles={modalConfig.styles}>
      {renderModalContent()}
    </Modal>
  )
}
